import React, { useMemo } from "react";
import { Field } from "react-final-form";
import InputText from "@components/Form/InputText";
import Select from "@components/Form/Select";
import {
  composeFormValidators,
  isRequired,
  isEmail,
  isPhoneNumber,
  hasMaxLength,
  isStreetAddress,
} from "@lib/validations";
import SelectSearch from "@components/Form/SelectSearch";

const INPUT_CLASS = "!max-w-full !min-w-0";

const CompanyDataSection = ({ form, apiData }) => {
  const { caenCodes, companyTypes, counties, cities } = apiData;

  const { values } = form.getState();

  const selectedCountyId = values?.companyDataSection?.address?.countyId;

  const caenCodeOptions = useMemo(
    () =>
      caenCodes
        ? caenCodes.map(({ code }) => ({ value: code, label: code }))
        : [],
    [caenCodes]
  );

  const citiesOptions = useMemo(
    () =>
      cities && cities[selectedCountyId]
        ? cities[selectedCountyId].map(({ id, name }) => ({
            value: id,
            label: name,
          }))
        : [],
    [cities, selectedCountyId]
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
      <div>
        <Field
          name="companyDataSection.companyName"
          component={InputText}
          label="Nume companie"
          required
          placeholder="Introdu numele companiei"
          inputClassName={INPUT_CLASS}
          labelClass="text-sm"
          validate={composeFormValidators(isRequired(), hasMaxLength(100))}
        />
      </div>

      <div>
        <Field
          name="companyDataSection.cif"
          component={InputText}
          label="CUI/CIF companie"
          required
          placeholder="Introdu CUI/CIF..."
          inputClassName={INPUT_CLASS}
          labelClass="text-sm"
          validate={composeFormValidators(isRequired(), hasMaxLength(20))}
        />
      </div>

      <div>
        <Field
          name="companyDataSection.caenCode"
          component={SelectSearch}
          label="Cod CAEN"
          required
          placeholder="alege.."
          controlClassName={INPUT_CLASS}
          labelClass="text-sm"
          validate={composeFormValidators(isRequired())}
          parse={(option) => option?.value ?? ""}
          format={(value) =>
            caenCodeOptions.find(
              (option) => String(option.value) === String(value)
            )
          }
          options={caenCodeOptions}
        />
      </div>

      <div>
        <Field
          name="companyDataSection.companyTypeId"
          label="Tip companie"
          required
          inputClassName={INPUT_CLASS}
          labelClass="text-sm"
          component={Select}
        >
          <option value="">alege...</option>

          {companyTypes &&
            companyTypes.map(({ type, id }) => (
              <option key={id} value={id}>
                {type}
              </option>
            ))}
        </Field>
      </div>

      <div>
        <Field
          name="companyDataSection.email"
          component={InputText}
          type="email"
          label="Email companie"
          required
          placeholder="Introdu email companie..."
          inputClassName={INPUT_CLASS}
          labelClass="text-sm"
          validate={composeFormValidators(isRequired(), isEmail())}
        />
      </div>

      <div>
        <Field
          name="companyDataSection.phone"
          component={InputText}
          type="tel"
          label="Telefon companie"
          required
          placeholder="Introdu telefon companie..."
          inputClassName={INPUT_CLASS}
          labelClass="text-sm"
          validate={composeFormValidators(isRequired(), isPhoneNumber())}
        />
      </div>

      <div>
        <Field
          name="companyDataSection.address.countyId"
          label="Judet companie"
          required
          inputClassName={INPUT_CLASS}
          labelClass="text-sm"
          component={Select}
          validate={composeFormValidators(isRequired())}
        >
          <option value="">alege...</option>

          {counties &&
            counties.map((county) => (
              <option key={county.id} value={county.id}>
                {county.name}
              </option>
            ))}
        </Field>
      </div>

      <div>
        <Field
          name="companyDataSection.address.cityId"
          component={SelectSearch}
          label="Localitate companie"
          placeholder="alege..."
          validate={composeFormValidators(isRequired())}
          controlClassName={INPUT_CLASS}
          labelClass="text-sm"
          required
          options={citiesOptions}
          parse={(option) => option?.value ?? ""}
          format={(value) =>
            citiesOptions.find(
              (option) => String(option.value) === String(value)
            )
          }
          disabled={!cities?.[selectedCountyId]}
        />
      </div>

      <div>
        <Field
          name="companyDataSection.address.streetName"
          component={InputText}
          label="Adresa companie"
          required
          placeholder="Introdu adresa completa"
          inputClassName={INPUT_CLASS}
          labelClass="text-sm"
          validate={composeFormValidators(
            isRequired(),
            hasMaxLength(100),
            isStreetAddress()
          )}
        />
      </div>

      <div>
        <Field
          name="companyDataSection.address.postalCode"
          component={InputText}
          label="Cod postal companie"
          placeholder="Introdu cod postal"
          inputClassName={INPUT_CLASS}
          labelClass="text-sm"
          validate={composeFormValidators(hasMaxLength(6), hasMaxLength(6))}
        />
      </div>
    </div>
  );
};

export default CompanyDataSection;
